/**
 * DailyInspirationsTableV2 — Daily Inspirations body, ported from the
 * prototype's `.screen-inspirations` (cms-redesign-mockup.html ~7870+,
 * calendar styles 2077–2197, list view 2199–2209).
 *
 * Two views, toggleable in the toolbar:
 *
 *   1. Calendar (default) — `.cal-wrap`. Month-at-a-glance grid with
 *      one cell per day. Cells with content show a 3-line preview +
 *      teal pin. Empty in-month cells show "+ Add inspiration" and
 *      click straight into /daily-inspirations/create with a `date`
 *      query param. The today cell is teal-tinted with a TODAY label.
 *      Month nav: ‹ / › / Today + a stat counter "N scheduled · M gaps".
 *   2. List (alt) — `.cal-list-wrap`. Chronological LibTable: date
 *      (formatted "May 1, 2026 · Fri"), title, ID, hover-revealed
 *      edit. Today row gets a teal-tinted background.
 *
 * Data: reuses the v1 `fetchAllDailyReflections` action — GET
 * /daily-inspirations, legacy `{objects, count}` envelope. The
 * reducer keeps the array on `state.daily_reflections.daily_reflections`,
 * and each row carries `daily_reflection_id`, `title`, and
 * `date_epoch` (UNIX seconds — multiply by 1000 for ms-since-epoch).
 *
 * No paginated mode — the dataset is small (one row per scheduled
 * day, currently ~365) and the calendar wants the full year visible
 * anyway.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  format,
  addDays,
  subDays,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  getDay,
  getDate,
  getDaysInMonth,
  setDate as setDayOfMonth,
} from 'date-fns';
import {
  fetchAllDailyReflections,
} from '../../../../actions/dailyReflectionActions';
import {
  LibTable,
  SegmentedControl,
  IconButton,
  cn,
} from '../../../redesign';
import DailyInspirationReassignModal from './DailyInspirationReassignModal';
import SeasonalChip from './SeasonalChip';

/* ─── Icons ────────────────────────────────────────────────────────── */

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

const CalendarViewIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8"  y1="2" x2="8"  y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ListViewIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="4" y1="6"  x2="20" y2="6"  />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="18" x2="20" y2="18" />
  </svg>
);

/* ─── Calendar helpers ──────────────────────────────────────────────── */

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Index inspirations by YYYY-MM-DD so the calendar grid can do
// constant-time lookups per cell. `date_epoch` is UNIX seconds.
function buildByDateMap(items) {
  const m = new Map();
  for (const it of items || []) {
    const epoch = Number(it.date_epoch);
    if (!Number.isFinite(epoch) || epoch <= 0) continue;
    const key = format(new Date(epoch * 1000), 'yyyy-MM-dd');
    // If the (unexpected) duplicate hits the same day, the earliest by
    // id wins — keeps the calendar stable across refreshes.
    if (!m.has(key)) m.set(key, it);
  }
  return m;
}

// Days that fill the visible month grid: leading prev-month padding,
// the month itself, then trailing next-month padding to land on a
// Saturday. Returns `{ date: Date, inMonth: boolean }[]`.
function gridDaysForMonth(viewMonth) {
  const first = startOfMonth(viewMonth);
  const last  = endOfMonth(viewMonth);
  const leadingPad  = getDay(first);         // 0=Sun → 0 pad days, 1=Mon → 1, …
  const trailingPad = 6 - getDay(last);      // 6=Sat → 0 pad days
  const days = [];

  for (let i = leadingPad; i > 0; i--) {
    days.push({ date: subDays(first, i), inMonth: false });
  }
  for (let i = 0; i < getDate(last); i++) {
    days.push({ date: addDays(first, i), inMonth: true });
  }
  for (let i = 1; i <= trailingPad; i++) {
    days.push({ date: addDays(last, i), inMonth: false });
  }
  return days;
}

/* ─── Calendar grid ─────────────────────────────────────────────────── */

function CalendarGrid({ viewMonth, byDate, onCellClick }) {
  const todayKey = format(new Date(), 'yyyy-MM-dd');
  const days = useMemo(() => gridDaysForMonth(viewMonth), [viewMonth]);

  return (
    <div style={{ padding: '0 28px 32px' }}>
      {/* `.cal-dow` — weekday header row, Asap Condensed uppercase. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 6,
          marginBottom: 6,
        }}
      >
        {DOW_LABELS.map((d) => (
          <div
            key={d}
            className="font-condensed font-bold uppercase text-text-muted text-center"
            style={{ fontSize: 11, letterSpacing: '0.08em', padding: '6px 0' }}
          >
            {d}
          </div>
        ))}
      </div>

      {/* `.cal-grid` — 7 columns, square cells. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 6,
        }}
      >
        {days.map(({ date, inMonth }) => {
          const key = format(date, 'yyyy-MM-dd');
          const item = byDate.get(key);
          const isToday = key === todayKey;

          if (!inMonth) {
            // `.cal-day.outside-month` — dimmed, non-interactive.
            return (
              <div
                key={key}
                aria-hidden
                className="bg-surface border border-sand"
                style={{
                  aspectRatio: '1 / 1',
                  borderRadius: 8,
                  padding: '8px 10px',
                  opacity: 0.4,
                  cursor: 'default',
                }}
              >
                <div
                  className="font-condensed font-bold text-text-muted"
                  style={{ fontSize: 16, lineHeight: 1, marginBottom: 8 }}
                >
                  {getDate(date)}
                </div>
              </div>
            );
          }

          // Scheduled day → Link into edit. Empty in-month day →
          // button that opens the reassign-picker modal so the admin
          // moves an existing inspiration onto this date (per design
          // intent: empty cells recycle, the page-header "+ New
          // Inspiration" is the create affordance).
          const empty = !item;

          // Feb 29 on a leap-year view is a reserved cell: the backend
          // picks a far-away non-seasonal DI at runtime
          // (DailyInspirationRepository::pickEvergreenFallback on
          // APIv2), so the cell has no permanent owner and shouldn't
          // be clickable. Render it as a dimmed informational tile
          // instead of either the edit-link or the reassign trigger.
          // JS months are 0-indexed; February is month 1.
          if (date.getMonth() === 1 && date.getDate() === 29) {
            return (
              <div
                key={key}
                aria-label="Feb 29: picked from the evergreen pool by the server on leap years"
                title="Feb 29 is picked automatically from the non-seasonal pool by the server on leap years."
                className="bg-cream border border-sand"
                style={{
                  aspectRatio: '1 / 1',
                  borderRadius: 8,
                  padding: '8px 10px',
                  opacity: 0.7,
                  cursor: 'not-allowed',
                  position: 'relative',
                }}
              >
                <div
                  className="font-condensed font-bold text-text-muted"
                  style={{ fontSize: 16, lineHeight: 1, marginBottom: 8 }}
                >
                  {getDate(date)}
                </div>
                <div
                  className="text-text-muted italic"
                  style={{ fontSize: 12, lineHeight: 1.35 }}
                >
                  Auto-picked from evergreen pool
                </div>
              </div>
            );
          }

          // Cell chrome — shared between Link and button variants.
          const cellClassName = cn(
            'flex flex-col no-underline border',
            empty
              ? 'border-sand'
              : isToday
                ? 'border-teal-green'
                : 'border-sand',
          );
          const cellStyle = {
            color: 'inherit',
            aspectRatio: '1 / 1',
            borderRadius: 8,
            padding: '8px 10px',
            position: 'relative',
            cursor: 'pointer',
            transition: 'all 140ms ease',
            background: isToday
              ? 'rgb(var(--color-teal-green) / 0.12)'    /* teal halo */
              : empty
                ? 'rgb(var(--color-cream) / 0.25)'      /* faint cream */
                : 'rgb(var(--color-surface))',           /* card surface */
            minWidth: 0,
            // Buttons inherit a font-family of `button`; reset so the
            // day number / preview text matches the Link variant.
            font: 'inherit',
            textAlign: 'left',
            width: '100%',
          };
          const hoverEnter = (e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow =
              '0 4px 10px rgb(var(--color-teal-green) / 0.12)';
            e.currentTarget.style.borderColor = 'rgb(var(--color-teal-green))';
            if (empty) {
              e.currentTarget.style.background = 'rgb(var(--color-cream) / 0.55)';
            }
          };
          const hoverLeave = (e) => {
            e.currentTarget.style.transform = '';
            e.currentTarget.style.boxShadow = '';
            if (!isToday) {
              e.currentTarget.style.borderColor = 'rgb(var(--color-sand))';
            }
            e.currentTarget.style.background = isToday
              ? 'rgb(var(--color-teal-green) / 0.12)'
              : empty
                ? 'rgb(var(--color-cream) / 0.25)'
                : 'rgb(var(--color-surface))';
          };

          /* Seasonal DIs get an amber pin (instead of the default teal)
           * so Kelly can see at a glance which days are date-tied —
           * matching the same "Seasonal" chip in the list view. */
          const isSeasonal = !empty && Boolean(item?.is_seasonal);
          const pinBg = isSeasonal ? '#D9A23A' : 'rgb(var(--color-teal-green))';
          const pinHalo = isSeasonal
            ? '0 0 0 3px rgba(217, 162, 58, 0.22)'
            : '0 0 0 3px rgb(var(--color-teal-green) / 0.2)';

          const cellInner = (
            <>
              {/* `.cal-day .pin` — teal (default) / amber (seasonal) dot
               * with halo, top-right. */}
              {!empty && (
                <span
                  aria-hidden
                  title={isSeasonal ? 'Seasonal — excluded from Feb 29 fallback' : undefined}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: pinBg,
                    boxShadow: pinHalo,
                  }}
                />
              )}
              <div
                className="font-condensed font-bold"
                style={{
                  fontSize: 16,
                  lineHeight: 1,
                  marginBottom: 8,
                  color: isToday
                    ? 'rgb(var(--color-teal-green-dark))'
                    : 'rgb(var(--color-navy))',
                }}
              >
                {getDate(date)}
                {isToday && (
                  <span
                    className="font-mono"
                    style={{
                      fontSize: 9.5,
                      letterSpacing: '0.08em',
                      color: 'rgb(var(--color-teal-green-dark))',
                      opacity: 0.85,
                      marginLeft: 5,
                    }}
                  >
                    · TODAY
                  </span>
                )}
              </div>
              <div
                className={cn(
                  empty ? 'text-text-muted italic' : 'text-text',
                  'font-semibold',
                )}
                style={{
                  fontSize: 13,
                  lineHeight: 1.35,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {empty ? 'Tap to assign…' : item.title || 'Untitled'}
              </div>
            </>
          );

          // Visual style: pad the cell's right side a little so the
          // 3-line title doesn't run under the edit pencil button.
          if (!empty) {
            cellStyle.paddingRight = 32;
          }

          /* Both occupied and empty cells trigger the swap/reassign
           * modal: occupied → swap (modal title "Swap with …"), empty →
           * reschedule (title "Reschedule to …"). The reassign modal
           * itself decides which based on whether the target slot has
           * an occupant. Per Kelly's intent for the perpetual model:
           * the calendar is for managing slot assignments, not for
           * editing the DI content — the explicit pencil button below
           * is the edit affordance.
           *
           * Rendered as `<div role="button">` rather than `<button>` so
           * the inner Link to /edit can sit nested without violating
           * the no-interactive-content-inside-button rule. Keyboard
           * Enter/Space mapped explicitly for accessibility. */
          const cellAriaLabel = empty
            ? `Assign an inspiration to ${format(date, 'MMMM d, yyyy')}`
            : `Swap "${item.title || 'Untitled'}" with another date`;

          return (
            <div
              key={key}
              role="button"
              tabIndex={0}
              onClick={() => onCellClick?.(key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onCellClick?.(key);
                }
              }}
              aria-label={cellAriaLabel}
              title={empty ? 'Click to assign an inspiration' : 'Click to swap with another date'}
              className={cellClassName}
              style={cellStyle}
              onMouseEnter={hoverEnter}
              onMouseLeave={hoverLeave}
            >
              {cellInner}

              {/* Edit affordance for occupied cells — explicit pencil so
               * Kelly can jump into the DI's form. stopPropagation
               * prevents the parent's swap click from also firing. */}
              {!empty && (
                <Link
                  to={`/daily-inspirations/${item.daily_reflection_id}/edit`}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  aria-label={`Edit "${item.title || 'Untitled'}"`}
                  title="Edit this inspiration"
                  className="absolute text-text-muted hover:text-teal-green-dark"
                  style={{
                    bottom: 6,
                    right: 6,
                    width: 22,
                    height: 22,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: 4,
                    background: 'rgb(var(--color-surface) / 0.85)',
                  }}
                >
                  {EditIconSvg}
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── List view ─────────────────────────────────────────────────────── */

function InspirationsListTable({ items }) {
  const todayKey = format(new Date(), 'yyyy-MM-dd');
  return (
    <div style={{ padding: '0 28px 32px' }}>
      <LibTable
        fixed
        className="border border-sand"
        style={{ borderRadius: 12, overflow: 'hidden' }}
      >
        <colgroup>
          <col style={{ width: '28%' }} />
          <col />
          <col style={{ width: 80 }} />
          <col style={{ width: 60 }} />
        </colgroup>
        <LibTable.Head>
          <tr>
            <LibTable.Th>Scheduled</LibTable.Th>
            <LibTable.Th>Title</LibTable.Th>
            <LibTable.Th>ID</LibTable.Th>
            <LibTable.Th aria-label="Actions"> </LibTable.Th>
          </tr>
        </LibTable.Head>
        <tbody>
          {items.map((it) => {
            const date = it.date_epoch
              ? new Date(it.date_epoch * 1000)
              : null;
            const key = date ? format(date, 'yyyy-MM-dd') : '';
            const isToday = key === todayKey;
            return (
              <LibTable.Row
                key={it.daily_reflection_id}
                style={isToday ? { background: '#75A29318' } : undefined}
              >
                <LibTable.Td>
                  <span
                    className={cn(
                      'font-mono',
                      isToday ? 'text-teal-green-dark font-semibold' : 'text-text-muted',
                    )}
                    style={{ fontSize: 12.5 }}
                  >
                    {date
                      ? `${format(date, 'MMM d, yyyy · EEE')}${isToday ? ' · TODAY' : ''}`
                      : '—'}
                  </span>
                </LibTable.Td>
                <LibTable.Td>
                  <Link
                    to={`/daily-inspirations/${it.daily_reflection_id}/edit`}
                    className={cn(
                      'font-semibold no-underline hover:text-teal-green-dark',
                      'truncate min-w-0',
                      'inline-flex items-center',
                      isToday ? 'text-teal-green-dark' : 'text-navy',
                    )}
                    style={{ fontSize: 13.5, gap: 8 }}
                    title={it.title || 'Untitled'}
                  >
                    <span className="truncate">{it.title || 'Untitled'}</span>
                    {it.is_seasonal ? <SeasonalChip /> : null}
                  </Link>
                </LibTable.Td>
                <LibTable.Td>
                  <span className="font-mono text-text-muted" style={{ fontSize: 12 }}>
                    #{it.daily_reflection_id}
                  </span>
                </LibTable.Td>
                <LibTable.Td style={{ textAlign: 'right' }}>
                  <div className="inline-flex opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                    <Link
                      to={`/daily-inspirations/${it.daily_reflection_id}/edit`}
                      aria-label={`Edit ${it.title}`}
                    >
                      <IconButton size="sm" aria-label="Edit">{EditIconSvg}</IconButton>
                    </Link>
                  </div>
                </LibTable.Td>
              </LibTable.Row>
            );
          })}
        </tbody>
      </LibTable>
    </div>
  );
}

/* ─── Toolbar (month nav + view toggle) ────────────────────────────── */

function MonthNav({ viewMonth, setViewMonth, scheduled, gaps }) {
  const arrowBtn = cn(
    'grid place-items-center bg-surface border border-sand text-text',
    'cursor-pointer hover:bg-cream hover:text-navy',
  );
  return (
    <div className="flex items-center" style={{ gap: 14 }}>
      <button
        type="button"
        className={arrowBtn}
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          fontSize: 16,
          lineHeight: 1,
          transition: 'all 140ms ease',
        }}
        title="Previous month"
        aria-label="Previous month"
        onClick={() => setViewMonth(subMonths(viewMonth, 1))}
      >
        ‹
      </button>
      <h2
        className="font-condensed font-bold text-navy"
        style={{ fontSize: 24, letterSpacing: '-0.015em', lineHeight: 1, margin: 0 }}
      >
        {format(viewMonth, 'MMMM yyyy')}
      </h2>
      <button
        type="button"
        className={arrowBtn}
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          fontSize: 16,
          lineHeight: 1,
          transition: 'all 140ms ease',
        }}
        title="Next month"
        aria-label="Next month"
        onClick={() => setViewMonth(addMonths(viewMonth, 1))}
      >
        ›
      </button>
      <button
        type="button"
        className="bg-surface border border-sand text-text hover:bg-cream hover:text-navy cursor-pointer font-semibold"
        style={{
          padding: '6px 12px',
          borderRadius: 8,
          fontSize: 12.5,
          transition: 'all 140ms ease',
        }}
        onClick={() => setViewMonth(startOfMonth(new Date()))}
      >
        Today
      </button>
      <span
        className="text-text-light font-mono"
        style={{ marginLeft: 4, fontSize: 12.5 }}
      >
        <strong className="text-navy font-bold">{scheduled}</strong> scheduled
        {' · '}
        <strong className="text-navy font-bold">{gaps}</strong> gap{gaps === 1 ? '' : 's'}
      </span>
    </div>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function DailyInspirationsTableV2() {
  const dispatch = useDispatch();
  const items = useSelector((s) => s.daily_reflections.daily_reflections);

  const [viewMonth, setViewMonth] = useState(() => startOfMonth(new Date()));
  const [view, setView] = useState('calendar');
  // The empty-cell click target — when set, opens the reassign modal
  // and identifies which date the picked inspiration should move to.
  const [reassignTarget, setReassignTarget] = useState(null);

  useEffect(() => {
    dispatch(fetchAllDailyReflections());
  }, [dispatch]);

  // Index by YYYY-MM-DD once per items change. Used by both views.
  const byDate = useMemo(() => buildByDateMap(items), [items]);

  // Counts for the toolbar stat: how many days in the visible month
  // have content vs. how many in-month days are blank.
  const { scheduled, gaps } = useMemo(() => {
    if (!items) return { scheduled: 0, gaps: 0 };
    const daysInMonth = getDaysInMonth(viewMonth);
    let s = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const key = format(setDayOfMonth(viewMonth, d), 'yyyy-MM-dd');
      if (byDate.has(key)) s += 1;
    }
    return { scheduled: s, gaps: daysInMonth - s };
  }, [items, viewMonth, byDate]);

  // List view sorts strictly by date_epoch ascending — chronological
  // is how the prototype shows it and how editors think about it.
  const sortedList = useMemo(() => {
    if (!items) return [];
    return [...items].sort(
      (a, b) => Number(a.date_epoch ?? 0) - Number(b.date_epoch ?? 0),
    );
  }, [items]);

  if (!items) {
    return (
      <div
        className="flex items-center justify-center text-text-muted"
        style={{ height: 256 }}
      >
        Loading daily inspirations…
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar — month nav (left) + view toggle (right). */}
      <div
        className="flex items-center justify-between"
        style={{ padding: '0 28px 18px', gap: 12, flexWrap: 'wrap' }}
      >
        <MonthNav
          viewMonth={viewMonth}
          setViewMonth={setViewMonth}
          scheduled={scheduled}
          gaps={gaps}
        />
        <SegmentedControl
          shape="md"
          value={view}
          onChange={setView}
          options={[
            { value: 'calendar', label: 'Calendar', icon: CalendarViewIcon },
            { value: 'list',     label: 'List',     icon: ListViewIcon },
          ]}
        />
      </div>

      {view === 'calendar' ? (
        <CalendarGrid
          viewMonth={viewMonth}
          byDate={byDate}
          onCellClick={(dateKey) => setReassignTarget(dateKey)}
        />
      ) : sortedList.length === 0 ? (
        <div
          className="bg-surface border border-sand text-text-muted italic text-center"
          style={{
            borderRadius: 12,
            padding: '40px 16px',
            fontSize: 13,
            margin: '0 28px 32px',
          }}
        >
          No daily inspirations yet — schedule one to get started.
        </div>
      ) : (
        <InspirationsListTable items={sortedList} />
      )}

      <DailyInspirationReassignModal
        open={reassignTarget != null}
        onClose={() => setReassignTarget(null)}
        targetDateKey={reassignTarget}
        inspirations={items}
      />
    </div>
  );
}
